import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaHeadset, FaWhatsapp, FaPhone } from "react-icons/fa";
import { useAuth } from "../hooks/use-auth";
import { ProfileDropdown, ConfirmationModal } from "./shared";
import NotificationBell from "./common/NotificationBell";
import NotificationPanel from "./common/NotificationPanel";
import BirthdayNotificationSnackbar from "./common/BirthdayNotificationSnackbar";
import { useBirthdayNotificationSnackbar } from "../hooks/useBirthdayNotificationSnackbar";
import { useBirthdayCheck } from "../hooks/useBirthdayCheck";

interface NavbarProps {
  onSidebarToggle?: (isOpen: boolean) => void;
  sidebarOpen?: boolean;
  showSidebar?: boolean;
}

const publicRoutes = ["/", "/about", "/plan-section", "/how-it-works", "/faq"];

const Navbar: React.FC<NavbarProps> = ({
  onSidebarToggle,
  sidebarOpen = true,
  showSidebar = false,
}) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [supportOpen, setSupportOpen] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, role, logout } = useAuth();

  // Check for customer birthdays on mount and generate notifications
  useBirthdayCheck();

  // Use birthday notification snackbar hook
  const { showSnackbar, snackbarMessage, closeSnackbar } =
    useBirthdayNotificationSnackbar();

  const [businessName, setBusinessName] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>("/Willovate_Resto_Logo.png");

  const isCustomerPage = useMemo(() => {
    if (!isAuthenticated && location.pathname.startsWith("/track-order")) {
      return true;
    }
    return location.pathname.startsWith("/restaurant") && !isAuthenticated;
  }, [location.pathname, isAuthenticated]);

  const isCustomerLoggedIn = Boolean(localStorage.getItem("customerToken"));

  // Check if sidebar should be shown
  const shouldShowSidebar = useMemo(() => {
    const planStatus = localStorage.getItem("plan_status");

    return (
      isAuthenticated &&
      role != null &&
      planStatus === "active" &&
      ["owner", "manager", "staff", "chef", "businessowner"].includes(
        role.toLowerCase(),
      ) &&
      !isCustomerPage
    );
  }, [isAuthenticated, role, isCustomerPage]);

  useEffect(() => {
    const fetchLogoFromBackend = async () => {
      const businessId = localStorage.getItem("businessId");
      const showLogoRoutes = [
        "/restaurant",
        "/main",
        "/dashboard",

        "/restaurant-profile",
        "/order-list",
        "/admin-menu-list",
        "/qr-generator",
        "/inventory",
        "/user-management",
        "/add-edit-dish",
        "/track-order",
        "/admin-profile",
        "/order-details",
        "/bill",
        "/admin",
        "/tables",
        "/customer-list",
        "/main",
      ];

      const path = location.pathname;
      const isPublicRoute = publicRoutes.includes(path);

      if (isPublicRoute) {
        setBusinessName("Willovate Resto");
        setLogoUrl("/Willovate_Resto_Logo.png");
        return;
      }

      if (
        businessId &&
        showLogoRoutes.some((route) => path.startsWith(route))
      ) {
        // Get business data from localStorage (set during login)
        const storedBusinessName = localStorage.getItem("businessName");
        const storedLogoUrl = localStorage.getItem("logoUrl");

        if (storedBusinessName) {
          setBusinessName(storedBusinessName);
        }
        if (storedLogoUrl) {
          setLogoUrl(storedLogoUrl);
        }
      }
    };

    fetchLogoFromBackend();
  }, [location.pathname]);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    sessionStorage.setItem("manual_logout", "true");
    logout(); // This now clears all localStorage items
    navigate("/login");
  };

  const toggleMobileMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleSidebarToggle = () => {
    if (onSidebarToggle) {
      onSidebarToggle(!sidebarOpen);
    }
  };

  // 24*7 Support Component
  const SupportDropdown = () => (
    <div className="relative">
      <button
        onClick={() => setSupportOpen(!supportOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <FaHeadset size={16} />
        <span className="text-sm font-medium">24×7 Support</span>
      </button>

      {supportOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-[9998]">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Get Instant Help
            </h3>
            <div className="space-y-2">
              <a
                href="https://wa.me/+919322346482?text=Hi,%20I%20need%20help%20with%20Willovate%20Resto"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-2 hover:bg-green-50 rounded-lg transition-colors"
                onClick={() => setSupportOpen(false)}
              >
                <FaWhatsapp className="text-green-500" size={20} />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    WhatsApp
                  </div>
                  <div className="text-xs text-gray-500">
                    Chat with us instantly
                  </div>
                </div>
              </a>

              <a
                href="tel:+919322346482"
                className="flex items-center space-x-3 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                onClick={() => setSupportOpen(false)}
              >
                <FaPhone className="text-blue-500" size={16} />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Call Us
                  </div>
                  <div className="text-xs text-gray-500">+91 93223 46482</div>
                </div>
              </a>

              <button
                onClick={() => {
                  setSupportOpen(false);
                  // Open email client
                  window.location.href =
                    "mailto:admin@willovate.in?subject=Willovate%20Resto%20Support%20Request";
                }}
                className="flex items-center space-x-3 p-2 hover:bg-purple-50 rounded-lg transition-colors w-full text-left"
              >
                <div className="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">@</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">Email</div>
                  <div className="text-xs text-gray-500">
                    admin@willovate.in
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500 text-center">
                Available 24/7 • Response within 30 mins
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Close support dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (supportOpen && !target.closest(".relative")) {
        setSupportOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [supportOpen]);

  // Customer navbar for restaurant pages
  if (isCustomerPage) {
    return (
      <nav className="bg-white shadow-md border-b border-gray-200 fixed top-0 left-0 right-0 z-40 h-14">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <div className="flex items-center">
            <img
              src={logoUrl}
              alt={businessName || "Restaurant"}
              className="h-10 w-auto object-contain"
              onError={(e) => {
                e.currentTarget.src = "/Willovate_Resto_Logo.png";
              }}
            />
          </div>

          {isCustomerLoggedIn && (
            <button
              onClick={() => {
                localStorage.removeItem("customerToken");
                window.location.reload();
              }}
              className="bg-red-600 text-white hover:bg-red-700 font-medium px-4 py-2 rounded-lg transition-colors duration-200"
            >
              Logout
            </button>
          )}
        </div>
      </nav>
    );
  }

  // Public routes navbar (only for logged-out users)
  if (!isAuthenticated && publicRoutes.includes(location.pathname)) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
        {/* Glassmorphism Container */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4">
          {/* Navbar Card */}
          <div className="relative rounded-2xl bg-white/80 backdrop-blur-xl shadow-lg ring-1 ring-gray-900/5 px-4 h-16 flex items-center justify-between">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => navigate("/")}
            >
              <img
                src="/Willovate_Resto_Logo.png"
                alt="Willovate Resto"
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  // Fallback to text if image fails
                  e.currentTarget.style.display = "none";
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.innerHTML =
                      '<span class="font-bold text-xl text-gray-900">Willovate<span class="text-orange-600">Resto</span></span>';
                  }
                }}
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
              >
                Home
              </button>
              <button
                onClick={() => navigate("/plan-section")}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate("/how-it-works")}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
              >
                Features
              </button>
              <button
                onClick={() => navigate("/faq")}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
              >
                FAQ
              </button>
            </div>

            {/* Right Side Actions */}
            <div className="hidden md:flex items-center space-x-3">
              <SupportDropdown />

              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => {
                      // Open demo popup
                      if ((window as any).openDemoPopup) {
                        (window as any).openDemoPopup();
                      }
                    }}
                    className="text-sm font-bold text-gray-700 px-4 py-2 hover:bg-gray-50 rounded-full transition-colors hidden lg:block"
                  >
                    Request Demo
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate("/login")}
                      className="text-sm font-bold text-gray-700 px-5 py-2.5 hover:text-orange-600 rounded-full transition-colors flex items-center gap-2"
                    >
                      Log in
                    </button>

                    <button
                      onClick={() => {
                        const planSection =
                          document.getElementById("plans-section");
                        if (planSection)
                          planSection.scrollIntoView({ behavior: "smooth" });
                        else navigate("/plan-section");
                      }}
                      className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2.5 rounded-full text-sm font-bold hover:shadow-lg hover:shadow-orange-200 hover:-translate-y-0.5 transition-all duration-200"
                    >
                      Get Started
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate("/main")}
                    className="bg-orange-50 text-orange-600 px-5 py-2 rounded-full text-sm font-bold hover:bg-orange-100 transition-colors border border-orange-100"
                  >
                    My Dashboard
                  </button>
                  <ProfileDropdown />
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              onClick={toggleMobileMenu}
            >
              <FaBars size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="absolute top-24 left-4 right-4 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform origin-top animate-in fade-in slide-in-from-top-4 duration-200 z-50">
            <div className="p-2 space-y-1">
              {[
                { label: "Home", path: "/" },
                { label: "Pricing", path: "/plan-section" },
                { label: "Features", path: "/how-it-works" },
                { label: "FAQ", path: "/faq" },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    navigate(item.path);
                    setMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-gray-600 font-medium hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors"
                >
                  {item.label}
                </button>
              ))}

              <div className="h-px bg-gray-100 my-2"></div>

              {!isAuthenticated ? (
                <div className="p-2 grid gap-2">
                  <button
                    onClick={() => {
                      if ((window as any).openDemoPopup) {
                        (window as any).openDemoPopup();
                      }
                      setMenuOpen(false);
                    }}
                    className="w-full py-3 text-center font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-xl border border-orange-200"
                  >
                    Get Demo
                  </button>
                  <button
                    onClick={() => {
                      navigate("/login");
                      setMenuOpen(false);
                    }}
                    className="w-full py-3 text-center font-bold text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-xl"
                  >
                    Log in
                  </button>
                  <button
                    onClick={() => {
                      navigate("/plan-section");
                      setMenuOpen(false);
                    }}
                    className="w-full py-3 text-center font-bold text-white bg-gray-900 hover:bg-gray-800 rounded-xl"
                  >
                    Get Started
                  </button>
                </div>
              ) : (
                <div className="p-2">
                  <button
                    onClick={() => {
                      navigate("/main");
                      setMenuOpen(false);
                    }}
                    className="w-full py-3 text-center font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-xl mb-2"
                  >
                    My Dashboard
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMenuOpen(false);
                    }}
                    className="w-full py-3 text-center font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl"
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Logout Confirmation Modal for Public Routes */}
        <ConfirmationModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={confirmLogout}
          type="logout"
        />
      </nav>
    );
  }

  // Admin/Dashboard navbar (with sidebar)
  if (shouldShowSidebar) {
    return (
      <>
        <nav className="bg-white shadow-md border-b border-gray-200 fixed top-0 left-0 right-0 z-40 h-14">
          <div className="flex items-center justify-between px-4 h-full">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSidebarToggle}
                className="text-gray-700 hover:text-blue-600 p-2 rounded-lg hover:bg-gray-100"
                aria-label="Toggle sidebar menu"
                title="Toggle sidebar menu"
              >
                <FaBars size={20} />
              </button>

              <div className="flex items-center">
                <div className="bg-white rounded-md p-1 shadow-sm flex items-center justify-center">
                  <img
                    src={logoUrl}
                    alt={businessName || "Willovate Resto"}
                    className="h-8 w-auto object-contain"
                    onError={(e) => {
                      e.currentTarget.src = "/Willovate_Resto_Logo.png";
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Birthday Notifications Bell */}
              <NotificationBell
                onNotificationClick={() => setIsNotificationPanelOpen(true)}
                className="hidden md:flex"
              />

              {/* 24*7 Support for Admin */}
              <SupportDropdown />

              {/* Profile Dropdown */}
              <ProfileDropdown />
            </div>
          </div>
        </nav>

        {/* Notification Panel */}
        <NotificationPanel
          isOpen={isNotificationPanelOpen}
          onClose={() => setIsNotificationPanelOpen(false)}
        />

        {/* Birthday Notification Snackbar */}
        <BirthdayNotificationSnackbar
          isOpen={showSnackbar}
          message={snackbarMessage}
          onClose={closeSnackbar}
          onNotificationClick={() => {
            setIsNotificationPanelOpen(true);
            closeSnackbar();
          }}
        />

        {/* Logout Confirmation Modal */}
        <ConfirmationModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={confirmLogout}
          type="logout"
        />
      </>
    );
  }

  // Fallback navbar
  return (
    <>
      <nav className="bg-white shadow-md border-b border-gray-200 fixed top-0 left-0 right-0 z-40 h-14">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src="/Willovate_Resto_Logo.png"
              alt="Willovate Resto"
              className="h-10 w-auto object-contain"
            />
          </div>

          {isAuthenticated && <ProfileDropdown />}
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
        type="logout"
      />
    </>
  );
};

export default Navbar;
